"use client";
import React from 'react';
import { Edit3, Trash2, Package, Video, Image as ImageIcon, Tag } from 'lucide-react';

const ProductCard = ({
  product,
  categories = [],
  subCategories = [],
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const primaryImage = product.images?.[0] ?? null;
  const hasMultipleImages = product.images?.length > 1;

  // Resolve names: use API-returned value, or look up from context (new products)
 // AFTER — handles both snake_case (API) and camelCase (context)
const resolvedCategoryId =
  product.category_id ?? product.categoryId ?? product.categoryID ?? '';

const resolvedSubCategoryId =
  product.sub_category_id ?? product.subcategory_id ?? product.subCategoryId ?? '';

const categoryName =
  product.category_name ||
  categories.find(c => String(c.id) === String(resolvedCategoryId))?.name ||
  '';

const subcategoryName =
  product.subcategory_name ||
  subCategories.find(s => String(s.id) === String(resolvedSubCategoryId))?.subcat_name ||
  '';

  const displayPrice =
    !product.your_price || product.your_price == 0
      ? product.max_retail_price
      : product.your_price;

  const discountPercent =
    product.max_retail_price > product.your_price && product.your_price > 0
      ? Math.round(((product.max_retail_price - product.your_price) / product.max_retail_price) * 100)
      : null;

  const stockStatus =
    product.stock > 10 ? { label: `${product.stock} in stock`, color: 'text-green-600' } :
    product.stock > 0  ? { label: `${product.stock} in stock`, color: 'text-yellow-600' } :
                         { label: 'Out of stock',               color: 'text-red-600'    };

  return (
    <div className="bg-white rounded-xl border border-orange-200 overflow-hidden hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300 transform hover:-translate-y-1 group">

      {/* Image */}
      <div className="relative w-full h-48">
        {primaryImage ? (
          <img src={primaryImage} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-300" />
          </div>
        )}

        {/* Status toggle */}
        <div className="absolute top-3 right-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={product.status === 'Active'}
              onChange={() => onToggleStatus(product.id)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors duration-300 shadow-md" />
            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
          </label>
        </div>

        {/* Media badges */}
        <div className="absolute bottom-2 left-2 flex gap-1">
          {hasMultipleImages && (
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow">
              <ImageIcon className="w-3 h-3 text-white" />
            </div>
          )}
          {product.video && (
            <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center shadow">
              <Video className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">

          {/* Left: category breadcrumb + name */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1 flex-wrap">
              <Tag className="w-3 h-3 text-orange-500 shrink-0" />
              <span className="text-xs text-orange-500 font-medium truncate">{categoryName}</span>
              {subcategoryName && (
                <>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-xs text-orange-400 truncate">{subcategoryName}</span>
                </>
              )}
              {product.model_name && (
                <>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-xs text-purple-400 truncate">{product.model_name}</span>
                </>
              )}
            </div>
            <h3 className="text-base font-semibold text-teal-800 group-hover:text-teal-900 line-clamp-1">
              {product.name}
            </h3>
          </div>

          {/* Right: price */}
          <div className="flex flex-col items-end shrink-0 gap-1 pt-0.5">
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-teal-800">₹{displayPrice}</span>
              {product.max_retail_price > product.your_price && product.your_price > 0 && (
                <span className="text-xs text-gray-400 line-through">₹{product.max_retail_price}</span>
              )}
            </div>
            {discountPercent && (
              <span className="text-xs font-semibold text-white bg-teal-500 px-2 py-0.5 rounded-full">
                {discountPercent}% off
              </span>
            )}
          </div>
        </div>

        {/* Footer: stock + actions */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1">
            <Package className="w-3 h-3 text-gray-400" />
            <span className={`text-xs font-medium ${stockStatus.color}`}>{stockStatus.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(product)}
              className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(product.id)}
              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;